/**
 * FE mirror of `x-layout-config`. Shared by `views` and `viewConfiguration`,
 * so it stays a leaf here instead of inside either feature.
 */

import { DEFAULT_PAGE_SIZE, resolveTablePageSize } from "@centric/data-table";

export type LayoutDisplayFormat =
  | "table"
  | "card-grid"
  | "list"
  | "properties"
  | "accordion"
  | "tabs"
  | "chart"
  | "timeline"
  | "kanban";

export interface TableOptions {
  sortable: boolean;
  density: "compact" | "comfortable" | "spacious";
  bordered: boolean;
  enableFiltering?: boolean;
  enableQuickFilter?: boolean;
  enableExport?: boolean;
  enableRowSelection?: boolean;
  enableColumnMenu?: boolean;
  enableTooltips?: boolean;
  pageSize?: number;
  /** Number of leading columns to freeze (pin left). c8 "Freeze columns". */
  freezeColumns?: number;
}

export interface CardOptions {
  cardsPerRow: number;
  cardSize: "small" | "medium" | "large";
  showLabels: boolean;
  orientation: "vertical" | "horizontal";
  spacing: "tight" | "normal" | "relaxed";
  thumbnailSize?: "small" | "medium" | "large" | "hero";
  showThumbnails?: boolean;
  documentLayout?: "gallery" | "thumbnail" | "icon";
  /** Card heading field. Unset → auto (schema display-name chain). */
  titleField?: string;
  /** Unlabeled line directly under the title. Unset → none. */
  subtitleField?: string;
}

export interface ListOptions {
  density: "compact" | "comfortable";
  showDividers: boolean;
  alternatingBg: boolean;
}

export interface ChartOptions {
  chartType: "bar" | "line" | "pie" | "donut" | "area";
  xAxis?: string;
  yAxis?: string;
  colorScheme: string;
  showLegend: boolean;
  showDataLabels: boolean;
}

export interface GlobalOptions {
  showPropertyLabels: boolean;
  propertyLabels?: Record<string, string>;
  /** INERT — carried for BE round-trip fidelity only; see `views/types.ts`. */
  colorTheme: "light" | "dark" | "auto";
  /** INERT — carried for BE round-trip fidelity only; see `views/types.ts`. */
  accentColor: string;
}

/** Cell style presets — the vocabulary the table's rule engine renders
 *  (`ROW_STYLE_PRESET_CLASS` in @centric/data-table). */
export type ConditionalRulePreset =
  | "red"
  | "green"
  | "yellow"
  | "blue"
  | "orange"
  | "faded"
  | "bold";

export interface ConditionalRule {
  /** Stable client-side ID for React list reconciliation (not persisted to BE). */
  _id?: string;
  property: string;
  condition: "equals" | "not-equals" | "contains" | "greater-than" | "less-than";
  value: string;
  /** Style preset applied to the matching cell. */
  preset?: ConditionalRulePreset;
  /**
   * On record (properties) surfaces, whether the tint applies to just the
   * matching field or its whole section. Ignored by the table renderer
   * (which always styles the row/cell). Omitted → "field".
   */
  scope?: "field" | "section";
  /** Legacy free-form style from configs saved before presets; `fontWeight`
   *  maps to the `bold` preset, anything else to `yellow`. */
  style?: {
    backgroundColor?: string;
    textColor?: string;
    fontWeight?: string;
    badge?: string;
  };
}

/** Options for the `matrix` display mode (nested 1:N group inside the parent
 *  row — C8 `IsMatrixGroup`). `headerField` is kept for round-trip of older
 *  configs and is unused at render. */
export interface MatrixDisplayOptions {
  /** Unused at render; persisted for older view configs. */
  headerField?: string;
  /** Cap on stacked related records per parent row. Unset = no cap. */
  maxColumns?: number;
}

/**
 * A workflow-backed action for a child-collection (create or row-edit). The
 * workflow form is rendered generically; `parentInput` / `recordInput` name the
 * workflow input fields seeded with the parent pk and the target row pk.
 */
export interface ChildCollectionWorkflowAction {
  definitionKey: string;
  /** Workflow input field seeded with the parent record's pk (create flow). */
  parentInput?: string;
  /** Workflow input field seeded with the target child pk (row-edit flow). */
  recordInput?: string;
}

/** One column of a child-collection table. `field` is a dotted path into the
 *  dereferenced child record (linked records expand under their resource name). */
export interface ChildCollectionColumn {
  field: string;
  label?: string;
  /** Registered cell-renderer key (e.g. `colour-rgb-hex-swatch`). */
  renderer?: string;
  /** Inline edit affordance for this column. */
  editAs?: "select" | "switch" | "number" | "text";
  /** Options for `editAs: "select"`. */
  options?: string[];
  /**
   * Contextual override only — drop this column in THIS location.
   *
   * Meaningful when the include names a base view (`viewName`): the child's own
   * view legitimately projects columns that are redundant here, e.g. a
   * ColorMaterial view showing `edges.Material.name` inside the Material you
   * are already looking at. Ignored when there is no base view to subtract from
   * (the column simply is not listed).
   */
  hidden?: boolean;
}

/**
 * Declarative config for a typed child-collection (list + create + edit +
 * delete of related records) on a parent schema. Authorable in the BE lens
 * (`x-ui-layout.relationships.includes[].childCollection`) so any parent/child
 * pair gets the same UX; an FE registry can supply a richer create dialog.
 */
export interface ChildCollectionConfig {
  /** Child resource (defaults to the relationship's `targetResource`). */
  resource?: string;
  version?: string;
  /** Panel heading noun (e.g. "Colours"). */
  title?: string;
  columns?: ChildCollectionColumn[];
  /**
   * Scalar back-reference on the child holding the parent's key, for children
   * that carry one instead of an `edges.{Parent}` projection (e.g.
   * `MaterialSample.colorMaterial`). Lets the panel scope itself with a plain
   * top-level filter — no edge needed in the index.
   */
  parentField?: string;
  /** Which parent property `parentField` holds. Defaults to `code`. */
  parentKey?: string;
  /** Dotted path resolved to a CSS colour for the grid swatch view. */
  swatchField?: string;
  createWorkflow?: ChildCollectionWorkflowAction;
  rowEditWorkflow?: ChildCollectionWorkflowAction;
  /** Workflow that removes a child row (runs BE side effects vs. a raw delete). */
  rowDeleteWorkflow?: ChildCollectionWorkflowAction;
  allowDelete?: boolean;
}

export interface RelationshipDisplayConfig {
  relationshipType: string;
  /**
   * Park switch — `false` keeps the config (field selection, mode, label) but
   * excludes the relation from save-time edge projections and rendering.
   * Absent means enabled (legacy configs predate the flag).
   */
  enabled?: boolean;
  displayMode: "inline" | "section" | "tab" | "reference" | "matrix";
  /** Resolved edge target (from the relationship catalog, `id` = "Resource:version").
   *  Required to project this relationship as a `UiLensDraft.edges[]` entry on save. */
  targetResource?: string;
  targetVersion?: string;
  /** Only meaningful when `displayMode === "matrix"`. */
  matrixConfig?: MatrixDisplayOptions;
  /**
   * DEAD — read from a legacy layout blob and round-tripped, nothing else.
   * It was never serialized to the lens and never read at render, and it
   * duplicated {@link label}, which IS the inline column prefix
   * (`{label} · {Property}`, see `flattenInlineEdges`). Its drawer control was
   * removed rather than wired. Do not build on this without also giving it a
   * distinct meaning from `label` and a path through `deriveDraftEdges`.
   */
  propertyPrefix?: string;
  selectedProperties?: string[];
  /**
   * Display name for the edge. Drives the inline column header prefix
   * (`{label} · {Property}`) and the record-page relationship tab. Absent means
   * unnamed — each consumer humanises its own fallback; do NOT default it to a
   * raw resource or relationship name, which renders verbatim as a machine name.
   */
  label?: string;
  viewName?: string;
  isDocumentRelationship?: boolean;
  /**
   * How clicking a related record of this relationship opens: full page or
   * side peek. Only consulted when the host view sets no `openRelatedIn` of its
   * own — that setting wins (see `resolveRelatedOpenIn`). Omit → `"page"`.
   */
  openIn?: "page" | "peek";
  /** Typed child-collection CRUD descriptor (lens-authorable, FE-overridable). */
  childCollection?: ChildCollectionConfig;
}

/** One collapsible section on a record-detail (properties) surface. */
export interface PropertiesGroupConfig {
  id: string;
  label: string;
  fields: string[];
  collapsedByDefault?: boolean;
}

/** Record-detail header configuration (title / subtitle / meta chips). */
export interface PropertiesHeaderOptions {
  titleField?: string;
  subtitleFields: string[];
  metaFields: string[];
}

/** "Properties" (record detail) display options. */
export interface PropertiesOptions {
  layout: "single-column" | "two-column" | "three-column";
  groups: PropertiesGroupConfig[];
  header?: PropertiesHeaderOptions;
}

/** Direction for a {@link SortRule}. */
export type SortDirection = "asc" | "desc";

/** A single column sort, used by the Data management tab. */
export interface SortRule {
  field: string;
  direction: SortDirection;
}

/**
 * One persisted column filter — TanStack `ColumnFiltersState` entry shape.
 *
 * @deprecated Legacy persisted shape (flat AND list). Still READ for saved
 * views written before the filter tree landed; new saves write
 * {@link SavedFilterGroup}.
 */
export interface SavedColumnFilter {
  id: string;
  value: unknown;
}

/** One persisted filter rule inside a {@link SavedFilterGroup}. */
export interface SavedFilterRule {
  field: string;
  /** Packed filter value — bare value or `{ op, value }` payload. */
  value: unknown;
}

/**
 * Persisted Notion-style filter tree node. Conjunction is uniform per group;
 * node ids are session-only identity and are intentionally NOT persisted
 * (persisting them would churn the saved blob on unrelated saves).
 */
export interface SavedFilterGroup {
  conjunction: "and" | "or";
  children: (SavedFilterRule | SavedFilterGroup)[];
}

/**
 * c8 "Report aggregations" (`reportSettings` / `AggregateInline`): a per-column
 * footer total. The string set mirrors `@centric/data-table`'s `BuiltinAggregator`
 * exactly so a string config value passes straight through to the table footer.
 */
export type ColumnAggregationFn = "sum" | "avg" | "count" | "min" | "max";

/**
 * Weighted average over the filtered corpus. Stored as an object so existing
 * views that persist a {@link ColumnAggregationFn} string keep loading.
 * Not written until `weightField` is chosen.
 */
export interface WeightedAverageAggregation {
  fn: "weightedAvg";
  weightField: string;
}

/** One column's saved calculation: a builtin name, or a weighted average. */
export type ColumnAggregationValue = ColumnAggregationFn | WeightedAverageAggregation;

const COLUMN_AGGREGATION_FN_VALUES: ReadonlySet<string> = new Set<ColumnAggregationFn>([
  "sum",
  "avg",
  "count",
  "min",
  "max",
]);

export const isColumnAggregationFn = (value: unknown): value is ColumnAggregationFn =>
  typeof value === "string" && COLUMN_AGGREGATION_FN_VALUES.has(value);

export const isWeightedAverageAggregation = (
  value: unknown,
): value is WeightedAverageAggregation => {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  const candidate = value as WeightedAverageAggregation;
  return (
    candidate.fn === "weightedAvg" &&
    typeof candidate.weightField === "string" &&
    candidate.weightField.length > 0
  );
};

/**
 * Sort, group, and filters persisted with the view. The live table seeds
 * them only where its renderer supports that.
 */
export interface DataManagementConfig {
  sortBy: SortRule[];
  groupBy: string[];
  /**
   * Table grouping presentation. `"inline"` (default) keeps grouped rows in one
   * table; `"sectioned"` renders one collapsible mini-table per group value.
   * Sectioned layout only activates for a single-level group (see
   * `isSectionedGrouping`); missing ⇒ inline.
   */
  groupLayout?: "inline" | "sectioned";
  /** Sectioned grouping starts expanded unless explicitly disabled. */
  expandGroupsByDefault?: boolean;
  /** Hide zero-row sections after filtering. Phase 1 only renders loaded groups. */
  hideEmptyGroups?: boolean;
  /** Show the jump-to chip strip for multi-section sectioned grouping. */
  showJumpToNav?: boolean;
  /** Optional row field whose first section value renders as a section badge. */
  sectionBadgeField?: string;
  /**
   * Per-column footer aggregation, authored on the composer's Calculations
   * screen. A {@link ColumnAggregationFn} string, or
   * `{ fn: "weightedAvg", weightField }`. Like {@link groupBy} it lives only
   * in the view's own config — the live table holds no state for it.
   */
  aggregations?: Record<string, ColumnAggregationValue>;
  /**
   * Filters authored on the view and seeded into the filter bar when it
   * loads. Legacy saves hold a flat `SavedColumnFilter[]` (implicit AND);
   * new saves hold a `SavedFilterGroup` tree. Readers accept both — see
   * `parseSavedFilters` in `~/features/views/utils/filterTree`.
   */
  // The deprecated arm is load-bearing: views saved before the filter tree
  // landed still hold the flat shape on disk, so the persisted type must keep
  // naming it. New saves write `SavedFilterGroup`.
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  filters?: SavedColumnFilter[] | SavedFilterGroup;
}

/** Persisted share vocabulary. The composer only writes `"everyone"`. */
export type ShareScope = "everyone" | "roles" | "just-me";

/**
 * View info stored on the layout. Title and description stay in
 * `viewSettings` until flat lens fields are sent
 * (`BE_SUPPORTS_FLAT_VIEW_METADATA`). `shareScope` and `isDefault` are
 * stored here and not enforced.
 */
export interface ViewSettingsConfig {
  /** Human-readable display name (`ViewSummary.title` / UiLens `title`). */
  title?: string;
  description?: string;
  shareScope: ShareScope;
  roles?: string[];
  isDefault?: boolean;
}

export interface LayoutConfig {
  displayFormat: LayoutDisplayFormat;
  tableOptions?: TableOptions;
  cardOptions?: CardOptions;
  listOptions?: ListOptions;
  chartOptions?: ChartOptions;
  propertiesOptions?: PropertiesOptions;
  globalOptions: GlobalOptions;
  conditionalFormatting?: ConditionalRule[];
  dataManagement?: DataManagementConfig;
  viewSettings?: ViewSettingsConfig;
  relationships?: {
    enabled: boolean;
    includes: RelationshipDisplayConfig[];
  };
}

// Mark the optional `dataManagement` / `viewSettings` / `relationships` keys as
// required on the default so consumers can read them as fallbacks without a
// non-null assertion, while every other field keeps its wide `LayoutConfig` type.
export const DEFAULT_LAYOUT_CONFIG: LayoutConfig &
  Required<Pick<LayoutConfig, "dataManagement" | "viewSettings" | "relationships">> = {
  displayFormat: "table",
  tableOptions: {
    sortable: true,
    density: "comfortable",
    bordered: true,
    enableQuickFilter: true,
    enableFiltering: true,
    enableExport: true,
    enableTooltips: true,
    pageSize: DEFAULT_PAGE_SIZE,
  },
  globalOptions: {
    showPropertyLabels: true,
    colorTheme: "auto",
    accentColor: "hsl(var(--primary))",
  },
  dataManagement: {
    sortBy: [],
    groupBy: [],
  },
  viewSettings: {
    shareScope: "everyone",
    isDefault: false,
  },
  relationships: {
    enabled: false,
    includes: [],
  },
  conditionalFormatting: [],
};

export interface SchemaRelationshipItem {
  id: string;
  [key: string]: unknown;
}

/** Maps legacy gallery page sizes (12/24/48/96) to the table's option set. */
const LEGACY_GALLERY_PAGE_SIZE: Record<number, number> = {
  12: 10,
  24: 25,
  48: 50,
  96: 100,
};

/**
 * Effective gallery page size — shares table defaults and upgrades saved legacy
 * values. Lives with the layout contract rather than in `views/types` because
 * `viewConfiguration` reads persisted `pageSize` on the authoring side and
 * `views` on the render side; it is the only value both need.
 */
export const resolveGalleryPageSize = (pageSize?: number): number => {
  if (pageSize !== undefined && pageSize in LEGACY_GALLERY_PAGE_SIZE) {
    return resolveTablePageSize(LEGACY_GALLERY_PAGE_SIZE[pageSize]);
  }

  return resolveTablePageSize(pageSize);
};
